// Map share module.
//
// Wires the topbar share button: freshens the URL with the current map state
// (via the caller-supplied syncUrl), asks the backend to mint a short
// `/share/<code>` link for it, then hands the short URL to the native share
// sheet on mobile, or opens a modal with the link and a copy button on
// desktop (styles: assets-src/scss/newmobilemap/components/_share-modal.scss).

import { showToast } from '../utils/toast';

const HTTP_TOO_MANY_REQUESTS = 429;
const MOBILE_BREAKPOINT_PX = 768;
const COPIED_FEEDBACK_MS = 2000;

function isMobile() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT_PX + 'px)').matches;
}

function copyToClipboard(url) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(url);
    }
    return new Promise(function (resolve, reject) {
        const area = document.createElement('textarea');
        area.value = url;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        try {
            document.execCommand('copy');
            resolve();
        } catch (error) {
            reject(error);
        } finally {
            document.body.removeChild(area);
        }
    });
}

let activeModal = null;

function closeShareModal() {
    if (!activeModal) return;
    if (activeModal.parentNode) activeModal.parentNode.removeChild(activeModal);
    document.removeEventListener('keydown', onModalKeydown);
    activeModal = null;
}

function onModalKeydown(e) {
    if (e.key === 'Escape') closeShareModal();
}

function openShareModal(url) {
    closeShareModal();

    const modal = document.createElement('div');
    modal.className = 'ms-modal nm-share-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Udostępnij widok mapy');
    modal.innerHTML =
        '<div class="ms-modal__overlay nm-share-modal__overlay"></div>'
        + '<div class="ms-modal__dialog nm-share-modal__dialog">'
        + '<div class="ms-modal__head">'
        + '<h3 class="ms-modal__title">Udostępnij widok mapy</h3>'
        + '<button type="button" class="ms-modal__close nm-share-modal__close" aria-label="Zamknij">&times;</button>'
        + '</div>'
        + '<div class="ms-modal__body nm-share-modal__body">'
        + '<p>Link prowadzi dokładnie do tego widoku mapy, razem z filtrami i przybliżeniem.</p>'
        + '<div class="nm-share-modal__row">'
        + '<input type="text" class="ms-input nm-share-modal__input" readonly aria-label="Link do widoku mapy">'
        + '<button type="button" class="ms-button ms-button--primary nm-share-modal__copy">Kopiuj</button>'
        + '</div>'
        + '</div>'
        + '</div>';

    const input = modal.querySelector('.nm-share-modal__input');
    input.value = url;

    modal.querySelector('.nm-share-modal__overlay').addEventListener('click', closeShareModal);
    modal.querySelector('.nm-share-modal__close').addEventListener('click', closeShareModal);

    input.addEventListener('focus', function () {
        input.select();
    });

    const copyBtn = modal.querySelector('.nm-share-modal__copy');
    copyBtn.addEventListener('click', function () {
        copyToClipboard(url).then(function () {
            input.select();
            copyBtn.textContent = 'Skopiowano';
            copyBtn.classList.add('is-copied');
            setTimeout(function () {
                copyBtn.textContent = 'Kopiuj';
                copyBtn.classList.remove('is-copied');
            }, COPIED_FEEDBACK_MS);
        }).catch(function () {
            showToast('Nie udało się skopiować linku.');
        });
    });

    document.body.appendChild(modal);
    document.addEventListener('keydown', onModalKeydown);
    activeModal = modal;

    input.focus();
    input.select();
}

function deliver(url) {
    if (isMobile() && navigator.share) {
        navigator.share({ title: document.title, url: url }).catch(function () {});
        return;
    }
    openShareModal(url);
}

export function initMapShare(options) {
    const opts = options || {};
    const triggerEl = opts.triggerEl;
    const syncUrl = typeof opts.syncUrl === 'function' ? opts.syncUrl : function () {};
    const apiUrl = opts.apiUrl || '/rcn/api/map-share';
    if (!triggerEl) return;

    let inFlight = false;

    triggerEl.addEventListener('click', async function () {
        if (inFlight) return;
        inFlight = true;
        triggerEl.disabled = true;
        try {
            // Flush the current map state into location.search first, so the
            // minted link reproduces exactly what the user is looking at.
            syncUrl();
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify({ query: location.search.replace(/^\?/, '') }),
            });
            if (res.status === HTTP_TOO_MANY_REQUESTS) {
                showToast('Przekroczono limit tworzenia linków. Spróbuj później.');
                return;
            }
            if (!res.ok) {
                showToast('Nie udało się utworzyć linku.');
                return;
            }
            const data = await res.json();
            if (!data || !data.url) {
                showToast('Nie udało się utworzyć linku.');
                return;
            }
            deliver(data.url);
        } catch (error) {
            showToast('Błąd sieci.');
        } finally {
            inFlight = false;
            triggerEl.disabled = false;
        }
    });
}
